
import { useState, useEffect, useMemo } from 'react';
import { MarkupCalculationResult, UseMarkupCalculationProps, MarkupCalculationHookReturn } from './types';
import { useCategoryName } from './useCategoryName';
import { calculateCustomTierMarkup, calculateCategoryMarkup, calculateGlobalMarkup } from './markupCalculationUtils';

/**
 * Hook to calculate the markup and margin for a given amount based on
 * applicable tier system (custom → category → global fallback logic)
 */
const useMarkupCalculation = ({
  amount,
  categoryId,
  companyId,
  customTiers = [],
  useCustomTiers = false
}: UseMarkupCalculationProps): MarkupCalculationHookReturn => {
  const [result, setResult] = useState<MarkupCalculationResult>({
    markup: 0,
    margin: 0,
    finalPrice: amount,
    sourceName: 'global',
    sourceLabel: 'Global Default'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Create a unique instance ID for this hook instance
  const instanceId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  // Memoize the calculation parameters to prevent unnecessary recalculations
  const calculationKey = useMemo(() => 
    `${amount}-${categoryId}-${companyId}-${useCustomTiers}-${JSON.stringify(customTiers)}`,
    [amount, categoryId, companyId, useCustomTiers, customTiers]
  );

  // Fetch category name when needed
  const categoryName = useCategoryName(categoryId, instanceId);

  // Main calculation effect - memoized to prevent infinite loops
  useEffect(() => {
    console.log(`[useMarkupCalculation:${instanceId}] Starting calculation with params:`, {
      amount,
      categoryId,
      companyId,
      useCustomTiers,
      customTiersLength: customTiers.length,
      calculationKey
    });

    // Skip calculation if amount is 0 or we don't have companyId
    if (amount === 0 || !companyId) {
      console.log(`[useMarkupCalculation:${instanceId}] Skipping calculation - amount is 0 or no companyId`);
      setResult({
        markup: 0,
        margin: 0,
        finalPrice: amount,
        sourceName: 'global',
        sourceLabel: 'Global Default'
      });
      setLoading(false);
      return;
    }

    let isCancelled = false;
    
    const calculateMarkup = async () => {
      try {
        if (isCancelled) {
          console.log(`[useMarkupCalculation:${instanceId}] Operation cancelled before starting`);
          return;
        }
        
        setLoading(true);
        setError(null);
        
        let calculatedResult: MarkupCalculationResult = {
          markup: 0,
          margin: 0,
          finalPrice: amount,
          sourceName: 'global',
          sourceLabel: 'Global Default'
        };
        
        // 1. Check for valid custom tiers first (line item-specific)
        const customResult = calculateCustomTierMarkup(customTiers, amount, useCustomTiers, instanceId);
        if (customResult && !isCancelled) {
          console.log(`[useMarkupCalculation:${instanceId}] Custom tier result:`, customResult);
          setResult(customResult);
          setLoading(false);
          return;
        }
        
        // 2. Check category tiers if we have a category ID
        if (categoryId && companyId && !isCancelled) {
          const categoryResult = await calculateCategoryMarkup(categoryId, companyId, amount, categoryName, instanceId);
          
          if (isCancelled) {
            console.log(`[useMarkupCalculation:${instanceId}] Operation cancelled after category lookup`);
            return;
          }
          
          if (categoryResult) {
            setResult(categoryResult);
            setLoading(false);
            return;
          }
        } else {
          console.log(`[useMarkupCalculation:${instanceId}] Skipping category check - no categoryId or companyId`);
        }
        
        // 3. Fallback to global default settings
        if (companyId && !isCancelled) {
          calculatedResult = await calculateGlobalMarkup(companyId, amount, instanceId);
        }
        
        if (!isCancelled) {
          console.log(`[useMarkupCalculation:${instanceId}] Final result:`, calculatedResult);
          setResult(calculatedResult);
        }
        
      } catch (err) {
        if (!isCancelled) {
          console.error(`[useMarkupCalculation:${instanceId}] Error calculating markup:`, err);
          setError(err instanceof Error ? err : new Error('Unknown error calculating markup'));
          
          // Set default values on error
          setResult({
            markup: 0,
            margin: 0,
            finalPrice: amount,
            sourceName: 'global',
            sourceLabel: 'Global Default (Error Fallback)'
          });
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    calculateMarkup();
    
    return () => {
      console.log(`[useMarkupCalculation:${instanceId}] Cleanup - cancelling operation`);
      isCancelled = true;
    };
  }, [calculationKey, categoryName, instanceId]);

  return { result, loading, error };
};

export default useMarkupCalculation;
